import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, Folder } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { WorkspaceLogo } from "@/components/admin/workspace-logo";
import { Table } from "@/components/application/table/table";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { RemoveFromWorkspaceButton } from "@/pages/admin/modals/remove-from-workspace-button";
import { SelectWorkspacesForMerchantModal } from "@/pages/admin/modals/select-workspaces-for-merchant-modal";
import { formatDate } from "@/utils/date-conversion";
import { TabHeaderReadOnly } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type WorkspacesTabProps = {
  merchant: MerchantWithUsers;
};

export const WorkspacesTab = ({ merchant }: WorkspacesTabProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 500 },
    { column: "name", direction: "asc" },
    {},
  );

  const enrolledWorkspaces = useMemo(
    () => (workspacesData?.workspaces ?? []).filter((ws) => ws.merchants.some((m) => m.id === merchant.id)),
    [workspacesData, merchant.id],
  );

  const enrolledWorkspaceIds = enrolledWorkspaces.map((ws) => ws.id);

  const totalPeople = useMemo(
    () => enrolledWorkspaces.reduce((sum, ws) => sum + ws.users.length, 0),
    [enrolledWorkspaces],
  );

  const subtitle = `${merchant.name} is in ${enrolledWorkspaces.length} workspaces and available to ${totalPeople} people`;

  return (
    <div className="flex flex-col gap-6 h-full">
      <TabHeaderReadOnly
        title="Workspaces"
        subtitle={subtitle}
        rightContent={
          <Button color="primary" onClick={() => setModalOpen(true)}>
            Add to Workspace
          </Button>
        }
      />

      <div className="border-t border-primary" />

      {enrolledWorkspaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 flex-1">
          <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
            <Folder className="size-6 text-secondary" />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-semibold text-primary">{merchant.name} isn't in any workspace</p>
            <p className="text-sm text-tertiary max-w-xs">
              Add this merchant to a workspace to give people access. Anyone in that workspace will be able to view it.
            </p>
          </div>
        </div>
      ) : (
        <Table aria-label="Workspaces">
          <Table.Header className="bg-transparent">
            <Table.Head id="name" label="Name" isRowHeader className="w-[50%]" />
            <Table.Head id="users" label="Users" className="w-[20%]" />
            <Table.Head id="created" label="Created" className="w-[20%]" />
            <Table.Head id="actions" label="" className="w-[10%]" />
          </Table.Header>

          <Table.Body items={enrolledWorkspaces}>
            {(ws) => (
              <Table.Row id={ws.id} className="bg-transparent">
                <Table.Cell className="whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <WorkspaceLogo name={ws.name} logoUrl={ws.logoUrl} className="size-8 rounded-md" />
                    <span className="text-sm font-medium text-primary">{ws.name}</span>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap text-sm text-tertiary">{ws.users.length}</Table.Cell>
                <Table.Cell className="whitespace-nowrap text-sm text-tertiary">{formatDate(ws.createdAt)}</Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <ButtonUtility
                      size="xs"
                      color="tertiary"
                      icon={ArrowUpRight}
                      tooltip="Go to workspace"
                      onClick={() => navigate({ to: "/admin/customers/$id", params: { id: ws.id } })}
                    />
                    <RemoveFromWorkspaceButton
                      workspaceId={ws.id}
                      merchantId={merchant.id}
                      name={ws.name}
                      removing="workspace"
                    />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}

      <SelectWorkspacesForMerchantModal
        merchantId={merchant.id}
        merchantName={merchant.name}
        enrolledWorkspaceIds={enrolledWorkspaceIds}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
};
